<script lang="ts">
  import { onMount } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { Confetti } from 'svelte-confetti';
  import type { Match } from '../types';

  export let match: Match;

  // Track score changes for confetti and sound effects
  let previousTeamScore = match.score_team;
  let previousOpponentScore = match.score_opponent;
  let showConfetti = false;
  let isMuted = false;
  let goalAudio: HTMLAudioElement | null = null;

  // Track score highlighting (for visual feedback on changes)
  let highlightTeamScore = false;
  let highlightOpponentScore = false;
  let teamHighlightTimeout: number | null = null;
  let opponentHighlightTimeout: number | null = null;

  // Effect configuration (extensible for future effects)
  const effectConfig = {
    confetti: {
      enabled: true,
      amount: 100,
      duration: 3000,
      colorArray: ['#ff0000', '#00ff00', '#0000ff', '#ffff00']
    },
    sound: {
      enabled: true,
      volume: 0.5,
      path: '/sounds/goal.mp3'
    }
  };

  // Reactive statement to detect team score changes
  $: {
    if (match.score_team !== previousTeamScore) {
      console.log('Score change detected:', {
        previous: previousTeamScore,
        new: match.score_team,
        timestamp: new Date().toISOString()
      });
      const isIncrease = match.score_team > previousTeamScore;
      triggerEffect('teamGoal', isIncrease);
      previousTeamScore = match.score_team;
    }
  }

  // Reactive statement to detect opponent score changes
  $: {
    if (match.score_opponent !== previousOpponentScore) {
      console.log('Opponent score change detected:', {
        previous: previousOpponentScore,
        new: match.score_opponent,
        timestamp: new Date().toISOString()
      });
      const isIncrease = match.score_opponent > previousOpponentScore;
      triggerEffect('opponentGoal', isIncrease);
      previousOpponentScore = match.score_opponent;
    }
  }

  // Extensible effect system
  function triggerEffect(eventType: string, isIncrease: boolean) {
    console.log('triggerEffect called:', { eventType, isIncrease, timestamp: new Date().toISOString() });

    // Highlight the score that changed (for 30 seconds) - both increases and decreases
    // This happens IMMEDIATELY so the score update is visible first
    if (eventType === 'teamGoal') {
      // Clear any existing timeout to prevent it from turning off the highlight
      if (teamHighlightTimeout !== null) {
        clearTimeout(teamHighlightTimeout);
      }
      highlightTeamScore = true;
      console.log('Team score highlight activated');
      teamHighlightTimeout = setTimeout(() => {
        highlightTeamScore = false;
        teamHighlightTimeout = null;
      }, 30000) as unknown as number; // 30 seconds
    } else if (eventType === 'opponentGoal') {
      // Clear any existing timeout to prevent it from turning off the highlight
      if (opponentHighlightTimeout !== null) {
        clearTimeout(opponentHighlightTimeout);
      }
      highlightOpponentScore = true;
      console.log('Opponent score highlight activated');
      opponentHighlightTimeout = setTimeout(() => {
        highlightOpponentScore = false;
        opponentHighlightTimeout = null;
      }, 30000) as unknown as number; // 30 seconds
    }

    // Delay sound and confetti slightly to ensure score is rendered first
    setTimeout(() => {
      console.log('Playing effects after delay:', { eventType, isIncrease });

      // Confetti only for team goals that increase the score
      if (eventType === 'teamGoal' && isIncrease && effectConfig.confetti.enabled) {
        showConfetti = true;
        console.log('Confetti started');
        setTimeout(() => {
          showConfetti = false;
        }, effectConfig.confetti.duration);
      }

      // Sound effect only when score INCREASES (not when goals are removed)
      if (isIncrease && (eventType === 'teamGoal' || eventType === 'opponentGoal')
          && effectConfig.sound.enabled && goalAudio && !isMuted) {
        goalAudio.currentTime = 0; // Reset for rapid consecutive goals
        console.log('Playing goal sound');
        goalAudio.play().catch(err => {
          // Silently handle autoplay restrictions
          console.log('Goal sound autoplay prevented:', err);
        });
      }
    }, 100); // 100ms delay to ensure visual update happens first
  }

  // Toggle mute state and persist to localStorage
  function toggleMute() {
    isMuted = !isMuted;
    localStorage.setItem('scoreboard-sound-muted', JSON.stringify(isMuted));
  }

  // Initialize previous scores and audio on mount
  onMount(() => {
    previousTeamScore = match.score_team;
    previousOpponentScore = match.score_opponent;

    // Load mute preference from localStorage
    const savedMutedState = localStorage.getItem('scoreboard-sound-muted');
    if (savedMutedState !== null) {
      isMuted = JSON.parse(savedMutedState);
    }

    // Initialize audio element
    if (effectConfig.sound.enabled) {
      goalAudio = new Audio(effectConfig.sound.path);
      goalAudio.volume = effectConfig.sound.volume;
    }
  });

  // Define highlight types and their icons (matching ScoreBoard.svelte)
  const highlightTypes = [
    { id: 'goal', label: $_('scoreBoard.highlights.goal'), icon: match.team.club.sport === 'hockey' ? '🏑' : '⚽' },
    { id: 'opponent-goal', label: $_('scoreBoard.highlights.opponentGoal'), icon: '❌' },
    { id: 'great-action', label: $_('scoreBoard.highlights.greatAction'), icon: '👏' },
    { id: 'great-save', label: $_('scoreBoard.highlights.greatSave'), icon: '🧤' },
    { id: 'substitution', label: $_('scoreBoard.highlights.substitution'), icon: '🔄' },
    { id: 'injury', label: $_('scoreBoard.highlights.injury'), icon: '❤️‍🩹' },
    { id: 'yellow-card', label: $_('scoreBoard.highlights.yellowCard'), icon: '🟨' },
    { id: 'red-card', label: $_('scoreBoard.highlights.redCard'), icon: '🟥' },
    { id: 'other', label: $_('scoreBoard.highlights.other'), icon: '📝' }
  ];

  function getHighlightIcon(typeId: string) {
    return highlightTypes.find(type => type.id === typeId)?.icon || '📝';
  }

  function getHighlightLabel(typeId: string) {
    return highlightTypes.find(type => type.id === typeId)?.label || 'Other';
  }

  // Helper to get player names from highlight actions
  function getPlayerNames(actions: any[], type: string) {
    if (type === 'goal') {
      const scorer = actions.find(a => a.action_type === 'goal-scorer')?.name || '';
      const assist = actions.find(a => a.action_type === 'assist')?.name || '';

      if (scorer && assist) {
        return `${scorer} (${assist})`;
      } else if (scorer) {
        return scorer;
      } else if (assist) {
        return `(${assist})`;
      }
      return '';
    }

    // For non-goal highlights
    const names = actions
      .map(action => action.name)
      .filter((name, index, self) => self.indexOf(name) === index);
    return names.join(', ');
  }

  // Sort highlights by index (descending - most recent first)
  $: sortedHighlights = match.highlights ? [...match.highlights].sort((a, b) => b.index - a.index) : [];
</script>

<div class="readonly-scoreboard">
  <!-- Confetti effect container -->
  {#if showConfetti}
    <div class="confetti-container">
      <Confetti
        amount={effectConfig.confetti.amount}
        duration={effectConfig.confetti.duration}
        colorArray={effectConfig.confetti.colorArray}
        cone={true}
        infinite={false}
      />
    </div>
  {/if}

  <div class="header-container">
    <h2 class="text-center mb-6">{$_('scoreBoard.liveScoreboard')}</h2>
    <button
      class="mute-button ghost secondary"
      on:click={toggleMute}
      aria-label={isMuted ? 'Unmute sound' : 'Mute sound'}
      title={isMuted ? 'Unmute sound' : 'Mute sound'}
    >
      {#if isMuted}
        <ion-icon name="volume-mute"></ion-icon>
      {:else}
        <ion-icon name="volume-medium"></ion-icon>
      {/if}
    </button>
  </div>

  <!-- Score Display -->
  <div class="score-board" class:away-game={match.location.toLowerCase() === 'away'}>
    <div class="team-container">
      <div class="club-name">{match.team.club.name}</div>
      <div class="team-logo">
        <img src={match.team.club.logo} alt={match.team.club.name} />
      </div>
      <div class="team-name">{match.team.name}</div>
      <div class="score-display" class:score-highlight={highlightTeamScore}>
        {match.score_team}
      </div>
    </div>

    <div class="score-divider">-</div>

    <div class="team-container">
      <div class="club-name">{match.opponent_club?.name}</div>
      <div class="team-logo">
        <img src={match.opponent_club?.logo} alt={match.opponent_club?.name} />
      </div>
      <div class="team-name">{match.opponent_team_name}</div>
      <div class="score-display" class:score-highlight={highlightOpponentScore}>
        {match.score_opponent}
      </div>
    </div>
  </div>

  <!-- Highlights List -->
  <h3 class="mt-6 mb-4">{$_('scoreBoard.highlights.title')}</h3>
  {#if sortedHighlights.length === 0}
    <p class="text-center text-muted">{$_('scoreBoard.noHighlights')}</p>
  {:else}
    <div class="highlights-list">
      {#each sortedHighlights as highlight}
        <article class="highlight-card">
          <div class="highlight-header">
            <div class="highlight-left">
              <span class="highlight-icon">{getHighlightIcon(highlight.type)}</span>
              <span class="highlight-label">{getHighlightLabel(highlight.type)}</span>
              {#if highlight.type === 'goal' || highlight.type === 'opponent-goal'}
                <span class="highlight-goal-number">{highlight.goal_index}</span>
              {/if}
            </div>
            {#if getPlayerNames(highlight.actions || [], highlight.type)}
              <div class="highlight-players">
                {getPlayerNames(highlight.actions || [], highlight.type)}
              </div>
            {/if}
          </div>
          {#if highlight.comment}
            <div class="highlight-comment">
              {highlight.comment}
            </div>
          {/if}
        </article>
      {/each}
    </div>
  {/if}
</div>

<style>
  .confetti-container {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
    z-index: 9999;
  }

  .readonly-scoreboard {
    padding: 1rem;
    max-width: 800px;
    margin: 0 auto;
  }

  .header-container {
    position: relative;
    margin-bottom: 1rem;
  }

  .mute-button {
    position: absolute;
    top: 0;
    right: 0;
    padding: 0.5rem;
    margin: 0;
    width: 2.5rem;
    height: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all 0.2s ease;
  }

  .mute-button:hover {
    background-color: var(--pico-secondary-hover-background);
  }

  .mute-button ion-icon {
    font-size: 1.5rem;
  }

  .score-board {
    display: flex;
    align-items: center;
    gap: 1rem;
    width: 100%;
    margin-bottom: 2rem;
  }

  .score-board.away-game {
    flex-direction: row-reverse;
  }

  .team-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
  }

  .club-name {
    font-size: 0.75rem;
    text-align: center;
    color: var(--pico-muted-color);
    margin-bottom: 0.25rem;
  }

  .team-logo {
    width: 30px;
    height: 30px;
    object-fit: contain;
  }

  .team-logo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .team-name {
    margin-bottom: 0.5rem;
    font-size: 1rem;
    font-weight: normal;
    text-align: center;
    color: var(--color-text);
    min-height: 30px;
  }

  .score-display {
    font-size: 2.5rem;
    font-weight: bold;
    background-color: transparent !important;
    padding: 0.5rem;
    width: 6rem;
    text-align: center;
    line-height: 1;
    margin: .15rem 0 .15rem 0;
    color: var(--pico-contrast);
    transition: background-color 0.3s ease, border-radius 0.3s ease;
    border-radius: 0;
  }

  .score-display.score-highlight {
    background-color: var(--pico-contrast) !important;
    color: var(--pico-contrast-inverse);
    border-radius: var(--pico-border-radius);
  }

  .score-divider {
    font-size: 4rem;
    font-weight: bold;
    margin: 0 1rem;
    padding-top: 3rem;
  }

  .highlights-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .highlight-card {
    padding: 1rem;
    border-radius: var(--pico-border-radius);
    background-color: var(--pico-card-background-color);
    margin-bottom: 0;
  }

  .highlight-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  .highlight-left {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .highlight-icon {
    font-size: 1.2rem;
  }

  .highlight-label {
    font-weight: 600;
  }

  .highlight-goal-number {
    font-size: 0.9rem;
    color: var(--pico-muted-color);
  }

  .highlight-players {
    font-weight: 500;
    color: var(--pico-primary);
  }

  .highlight-comment {
    margin-top: 0.5rem;
    padding: 0.75rem;
    border-radius: 4px;
    font-style: italic;
    color: var(--pico-muted-color);
    font-weight: 400;
  }

  .text-muted {
    color: var(--pico-muted-color);
  }
</style>
